class CarRegistrationLookup extends HTMLElement {
  constructor() {
    super();
    this.apiUrl = this.dataset.apiUrl || '';
    this.form = this.querySelector('form');
    this.countrySelect = this.querySelector('[name="country"]');
    this.stateSelect = this.querySelector('[name="state"]');
    this.stateWrapper = this.querySelector('[data-state-wrapper]');
    this.plateInput = this.querySelector('[name="plateNumber"]');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.errorMessage = this.querySelector('[role="alert"]');
    this.resultsContainer = this.querySelector('.car-registration-results');
    this.discountPercent = parseFloat(this.dataset.discountPercent || '0');
    this.fitBadgeLabel = this.dataset.fitLabel || 'Guaranteed Fit';
    this.fallbackBadgeLabel = this.dataset.fallbackLabel || 'Universal Fit';
    this.fallbackCopy = this.dataset.fallbackCopy || "We don't have a custom-made set for your vehicle yet, but our universal seat covers fit almost perfectly.";
    
    this.validationState = {
      plate: { isValid: false, message: '' },
      country: { isValid: true },
      state: { isValid: false }
    };
    
    this.init();
  }

  init() {
    if (!this.form) return;

    this.setupInput();
    this.setupInteractions();
    this.loadVehicleData();

    if (this.countrySelect) {
      this.countrySelect.addEventListener('change', () => this.handleCountryChange());
      // Initial check without animation
      this.handleCountryChange(false);
    }

    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    const clearButton = this.querySelector('[data-clear-vehicle]');
    if (clearButton) {
      clearButton.addEventListener('click', () => this.clearVehicleData());
    }
    
    // Entrance Animation
    this.animateEntrance();
  }

  animateEntrance() {
    if (typeof gsap !== 'undefined') {
      gsap.from(this.querySelector('form'), {
        y: 20,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
        delay: 0.2
      });
    }
  }

  setupInput() {
    if (!this.plateInput) return;
    
    this.plateInput.addEventListener('input', (e) => {
      e.target.value = e.target.value.toUpperCase();
      this.hideError();
    });
  }

  setupInteractions() {
    // Focus management simulating Radix primitive behavior
    const inputs = [this.countrySelect, this.stateSelect, this.plateInput].filter(Boolean);
    
    inputs.forEach(input => {
        input.addEventListener('focus', () => {
            const wrapper = input.closest('.group');
            if (wrapper && typeof gsap !== 'undefined') gsap.to(wrapper, { scale: 1.02, duration: 0.2 });
        });
        input.addEventListener('blur', () => {
             const wrapper = input.closest('.group');
             if (wrapper && typeof gsap !== 'undefined') gsap.to(wrapper, { scale: 1, duration: 0.2 });
        });
    });

    if (this.countrySelect && this.stateSelect) {
       this.countrySelect.addEventListener('change', () => {
          if (this.countrySelect.value === 'AU') {
             setTimeout(() => this.stateSelect.focus(), 300);
          } else {
             setTimeout(() => this.plateInput.focus(), 100);
          }
       });
    }
    
    if (this.stateSelect) {
        this.stateSelect.addEventListener('change', () => {
            setTimeout(() => this.plateInput.focus(), 100);
        });
    }
  }

  handleCountryChange(animate = true) {
    if (!this.stateWrapper) return;
    
    const isAU = this.countrySelect?.value === 'AU';
    
    if (isAU) {
      this.stateWrapper.classList.remove('hidden');
      if (this.stateSelect) {
          this.stateSelect.setAttribute('required', 'required');
          this.validationState.state.isValid = false;
      }
      
      if (animate && typeof gsap !== 'undefined') {
        gsap.fromTo(this.stateWrapper, 
          { autoAlpha: 0, x: -20, display: 'none' },
          { autoAlpha: 1, x: 0, display: 'block', duration: 0.4, ease: 'back.out(1.7)' }
        );
      } else {
        this.stateWrapper.style.opacity = '1';
        this.stateWrapper.style.display = 'block';
        this.stateWrapper.style.transform = 'none';
      }
    } else {
      if (this.stateSelect) {
          this.stateSelect.removeAttribute('required');
          this.validationState.state.isValid = true;
      }

      if (animate && typeof gsap !== 'undefined') {
        gsap.to(this.stateWrapper, {
           autoAlpha: 0,
           x: -10,
           duration: 0.3,
           onComplete: () => {
               this.stateWrapper.classList.add('hidden');
               this.stateWrapper.style.display = 'none';
           }
        });
      } else {
        this.stateWrapper.classList.add('hidden');
        this.stateWrapper.style.display = 'none';
      }
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    if (!this.apiUrl) {
      this.showError('API URL not configured');
      return;
    }

    const plateValue = this.plateInput.value.trim().replace(/[^A-Z0-9]/g, '').toUpperCase();
    
    if (!plateValue || plateValue.length < 1) {
      this.showError('Please enter a registration number');
      return;
    }
    
    if (this.countrySelect?.value === 'AU' && !this.stateSelect?.value) {
      this.showError('Please select a state');
      return;
    }

    this.setLoading(true);
    this.hideError();
    this.hideResults();

    try {
      const response = await fetch(`${this.apiUrl}/api/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          country: this.countrySelect.value,
          state: this.countrySelect.value === 'AU' ? this.stateSelect.value : null,
          plateNumber: plateValue
        })
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Failed to lookup vehicle');

      if (data.success && data.vehicle) {
        await this.showResults(data);
        this.saveVehicleData(data);
        this.dispatchVehicleFoundEvent(data.vehicle);
      } else {
        throw new Error('Vehicle not found');
      }
    } catch (error) {
      this.showError(error.message);
    } finally {
      this.setLoading(false);
    }
  }

  async showResults(data) {
    if (!this.resultsContainer) return;
    
    // Support both old format (data = vehicle) and new format (data = { vehicle, products })
    const vehicle = data.vehicle || data;
    const products = data.products || [];

    const elements = {
      make: this.resultsContainer.querySelector('[data-vehicle-make]'),
      model: this.resultsContainer.querySelector('[data-vehicle-model]'),
      year: this.resultsContainer.querySelector('[data-vehicle-year]'),
      desc: this.resultsContainer.querySelector('[data-vehicle-description]'),
      descWrapper: this.resultsContainer.querySelector('[data-vehicle-description-wrapper]'),
      badge: this.resultsContainer.querySelector('[data-fit-badge]'),
      universalNote: this.resultsContainer.querySelector('[data-universal-note]'),
      productsGrid: this.resultsContainer.querySelector('[data-products-grid]')
    };

    if (elements.make) elements.make.textContent = vehicle.make || '';
    if (elements.model) elements.model.textContent = vehicle.model || '';
    if (elements.year) elements.year.textContent = vehicle.year || '';
    
    if (elements.desc && vehicle.description) {
      elements.desc.textContent = vehicle.description;
      elements.descWrapper?.classList.remove('hidden');
    } else {
      elements.descWrapper?.classList.add('hidden');
    }

    const isFallback = Boolean(data.fallbackUsed);
    const fitLabel = this.fitBadgeLabel || 'Guaranteed Fit';
    const fallbackLabel = this.fallbackBadgeLabel || 'Universal Fit';
    const fallbackCopy = this.fallbackCopy || "We don't have a custom-made set for your vehicle yet, but our universal seat covers fit almost perfectly.";

    if (elements.badge) {
      elements.badge.textContent = isFallback ? fallbackLabel : fitLabel;
      elements.badge.classList.toggle('sk-rego-badge--fit', !isFallback);
      elements.badge.classList.toggle('sk-rego-badge--uni', isFallback);
    }

    if (elements.universalNote) {
      if (isFallback) {
        elements.universalNote.textContent = fallbackCopy;
        elements.universalNote.classList.remove('hidden');
      } else {
        elements.universalNote.classList.add('hidden');
      }
    }

    // Render Products
    if (elements.productsGrid) {
        elements.productsGrid.innerHTML = ''; // Clear existing
        
        if (products.length > 0) {
            const hydratedProducts = await Promise.all(products.map((product) => this.hydrateProduct(product)));
            this.renderProducts(hydratedProducts, elements.productsGrid, vehicle, { isFallback });
        } else {
            // Fallback: Search in Shopify Store
            await this.fetchStoreProducts(vehicle, elements.productsGrid);
        }
    }

    this.resultsContainer.classList.remove('hidden');
    
    if (typeof gsap !== 'undefined') {
       const tl = gsap.timeline();
       tl.fromTo(this.resultsContainer, 
         { height: 0, opacity: 0 },
         { height: 'auto', opacity: 1, duration: 0.5, ease: 'power3.out' }
       )
       .from(this.resultsContainer.children, {
          y: 20,
          opacity: 0,
          stagger: 0.1,
          duration: 0.4
       }, "-=0.3");
    }
  }

  renderProducts(products, container, vehicle = {}, options = {}) {
     const { isFallback = false } = options;
     container.innerHTML = '';
     
     if (!products.length) {
        container.parentElement.classList.add('hidden');
        return;
     }

     products.forEach(product => {
        const productCard = document.createElement('div');
        productCard.className = 'sk-rego-item';

        let imgUrl = '';
        if (product.featuredImage && product.featuredImage.url) {
             imgUrl = product.featuredImage.url.includes('?') ? product.featuredImage.url : `${product.featuredImage.url}?width=400`;
        }

        const makeText = (product.make?.value || vehicle.make || '').trim();
        const modelText = (product.model?.value || vehicle.model || '').trim();
        const yearText = (product.year?.value || vehicle.year || '').trim();

        const fitDetails = [yearText, makeText, modelText].filter(Boolean).join(' • ');
        const fitCopy = [yearText, makeText, modelText].filter(Boolean).length ? fitDetails : (isFallback ? 'Universal fit for most vehicles' : 'Fits your vehicle');
        const badgeText = isFallback ? (this.fallbackBadgeLabel || 'Universal Fit') : (this.fitBadgeLabel || 'Guaranteed Fit');
        const originalPrice = this.parsePrice(product);
        const discountedPrice = originalPrice && this.discountPercent > 0
          ? this.applyDiscount(originalPrice, this.discountPercent)
          : originalPrice;
        const priceMarkup = this.buildPriceMarkup(originalPrice, discountedPrice);
        
        productCard.innerHTML = `
          <a href="/products/${product.handle}" class="sk-rego-item-link">
             <div class="sk-rego-item-thumb">
                ${imgUrl ? `<img src="${imgUrl}" alt="${product.featuredImage?.altText || product.title}" loading="lazy">` : '<svg width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>'}
             </div>
             <div class="sk-rego-item-info">
                <div class="sk-rego-item-title">${product.title}</div>
                <div class="sk-rego-item-fit">${fitCopy}</div>
                ${priceMarkup}
             </div>
             <span class="sk-rego-item-badge${isFallback ? ' sk-rego-item-badge--uni' : ''}">${badgeText}</span>
          </a>
        `;
        container.appendChild(productCard);
     });
     container.parentElement.classList.remove('hidden');
  }

  buildPriceMarkup(originalPrice, discountedPrice) {
    const hasDiscount = this.discountPercent > 0;
    const badge = hasDiscount ? `<span class="product-card__discount-badge">-${this.discountPercent}% OFF</span>` : '';

    if (!originalPrice) {
      return hasDiscount
        ? `<div class="product-card__price product-card__price--badge-only">${badge}</div>`
        : '';
    }

    const current = this.formatMoney(discountedPrice ?? originalPrice);
    const original = hasDiscount ? `<span class="product-card__price-original">${this.formatMoney(originalPrice)}</span>` : '';

    return `
      <div class="product-card__price">
        <span class="product-card__price-current">${current}</span>
        ${original}
        ${badge}
      </div>
    `;
  }

  parsePrice(product = {}) {
    if (typeof product.price === 'number') return product.price;
    if (typeof product.price === 'string') return this.moneyToCents(product.price);
    if (typeof product.price_min === 'number') return product.price_min;
    if (product.priceRange?.minVariantPrice?.amount) return this.moneyToCents(product.priceRange.minVariantPrice.amount);
    if (Array.isArray(product.variants) && product.variants.length) {
      const variantPrice = product.variants[0].price ?? product.variants[0].priceV2?.amount;
      if (variantPrice) return this.moneyToCents(variantPrice);
    }
    return null;
  }

  moneyToCents(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') {
      return value > 1000 ? Math.round(value) : Math.round(value * 100);
    }
    const numeric = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    if (Number.isNaN(numeric)) return null;
    return numeric > 1000 ? Math.round(numeric) : Math.round(numeric * 100);
  }

  formatMoney(valueInCents) {
    if (valueInCents === null || valueInCents === undefined) return '';
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(valueInCents);
    }
    return `$${(valueInCents / 100).toFixed(2)}`;
  }

  applyDiscount(originalPrice, percent) {
    const safePercent = Math.min(Math.max(percent, 0), 100);
    return Math.round(originalPrice * (1 - safePercent / 100));
  }

  async hydrateProduct(product = {}) {
    if (product.price || product.priceRange || (Array.isArray(product.variants) && product.variants.length)) {
      return product;
    }

    const handle = product.handle || product.url?.split('/products/')[1]?.split('?')[0];
    if (!handle) return product;

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return product;

      const data = await response.json();
      return {
        ...product,
        title: product.title || data.title,
        handle: data.handle,
        featuredImage: product.featuredImage || { url: data.featured_image, altText: data.title },
        price: typeof data.price === 'number' ? data.price : this.moneyToCents(data.price),
        priceRange: data.price_range || null,
        variants: data.variants
      };
    } catch (error) {
      console.warn('Unable to hydrate product', error);
      return product;
    }
  }

  async fetchStoreProducts(vehicle, container) {
     if (!vehicle.make) return;
     
     // Show searching message
     container.parentElement.classList.remove('hidden');
     container.innerHTML = '<div class="sk-rego-msg sk-rego-msg--pulse">Searching for compatible parts...</div>';

     try {
         const query = `${vehicle.make} ${vehicle.model} ${vehicle.year}`.trim();
         const response = await fetch(`/search?type=product&q=${encodeURIComponent(query)}&section_id=main-search`);
         const text = await response.text();
         
         const parser = new DOMParser();
         const doc = parser.parseFromString(text, 'text/html');
         
         const products = [];
         // Select grid items from the search results
         const items = doc.querySelectorAll('.grid__item');
         
         items.forEach(item => {
             // Attempt to extract data from typical theme structure
             const titleEl = item.querySelector('.card__heading, .product-card__title, h3, h2 a');
             const linkEl = item.querySelector('a[href*="/products/"]');
             const imgEl = item.querySelector('img');
             
             if (titleEl && linkEl) {
                 // Helper to get handle from URL
                 const handle = linkEl.getAttribute('href').split('/products/')[1]?.split('?')[0] || linkEl.getAttribute('href');
                 
                 products.push({
                     title: titleEl.textContent.trim(),
                     handle: handle,
                     featuredImage: {
                         url: imgEl ? imgEl.src : null,
                         altText: imgEl ? imgEl.alt : ''
                     }
                 });
             }
         });
         
        if (products.length > 0) {
            const hydratedProducts = await Promise.all(products.map((product) => this.hydrateProduct(product)));
            this.renderProducts(hydratedProducts, container, vehicle, { isFallback: false });
         } else {
             container.innerHTML = `<div class="sk-rego-msg">No compatible parts found for ${vehicle.year} ${vehicle.make} ${vehicle.model}</div>`;
         }
         
     } catch (e) {
         console.error('Shopify search failed:', e);
         container.innerHTML = '';
         container.parentElement.classList.add('hidden');
     }
  }

  hideResults() {
    if (this.resultsContainer) {
      this.resultsContainer.classList.add('hidden');
      // Reset styles if using GSAP
      this.resultsContainer.style.height = '';
      this.resultsContainer.style.opacity = '';
    }
  }

  showError(message) {
    if (!this.errorMessage) return;
    
    const text = this.errorMessage.querySelector('.error-text');
    if (text) text.textContent = message;
    
    this.errorMessage.classList.remove('hidden');
    
    if (typeof gsap !== 'undefined') {
      gsap.fromTo(this.errorMessage,
        { y: -10, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.3 }
      );
      
      // Shake effect
      gsap.to(this.form, {
        x: [-5, 5, -5, 5, 0],
        duration: 0.4,
        ease: 'power3.inOut'
      });
    }
    
    this.plateInput?.focus();
  }

  hideError() {
    if (this.errorMessage) {
      this.errorMessage.classList.add('hidden');
    }
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    
    this.submitButton.disabled = isLoading;
    this.submitButton.setAttribute('data-loading', isLoading);
    
    const loader = this.submitButton.querySelector('[data-loader]');
    if (loader) {
       // Toggle logic handled by CSS group-data-[loading] usually, 
       // but we can force GSAP if we want complex exit/enter
    }
  }

  saveVehicleData(payload) {
    try {
      localStorage.setItem('sharkskin_vehicle', JSON.stringify(payload));
      localStorage.setItem('sharkskin_form_state', JSON.stringify({
        country: this.countrySelect?.value,
        state: this.stateSelect?.value
      }));
    } catch (e) { console.warn(e); }
  }

  async loadVehicleData() {
    try {
      const saved = localStorage.getItem('sharkskin_vehicle');
      const formState = localStorage.getItem('sharkskin_form_state');
      
      if (formState) {
        const state = JSON.parse(formState);
        if (this.countrySelect && state.country) {
          this.countrySelect.value = state.country;
          this.handleCountryChange(false);
        }
        if (this.stateSelect && state.state) {
           setTimeout(() => { this.stateSelect.value = state.state; }, 100);
        }
      }
      
      if (saved) {
        const parsed = JSON.parse(saved);
        await this.showResults(parsed);
      }
    } catch (e) { console.warn(e); }
  }

  clearVehicleData() {
    localStorage.removeItem('sharkskin_vehicle');
    localStorage.removeItem('sharkskin_form_state');
    this.hideResults();
    this.form.reset();
    this.handleCountryChange(true);
    this.dispatchVehicleClearedEvent();
    this.plateInput?.focus();
  }

  dispatchVehicleFoundEvent(vehicle) {
    this.dispatchEvent(new CustomEvent('vehicle-found', { detail: { vehicle }, bubbles: true }));
  }
  
  dispatchVehicleClearedEvent() {
    this.dispatchEvent(new CustomEvent('vehicle-cleared', { bubbles: true }));
  }
}

if (!customElements.get('car-registration-lookup')) {
  customElements.define('car-registration-lookup', CarRegistrationLookup);
}
window.CarRegistrationLookup = CarRegistrationLookup;
